import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { MovieCard, MovieCardSkeleton } from './MovieCard';

export const MovieRow = ({ title, films, loading = false }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll);
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', checkScroll); ro.disconnect(); };
  }, [checkScroll, films, loading]);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const scrollAmount = direction === 'left' ? -600 : 600;
    scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  };

  return (
    <section className="category-row relative" data-testid={`movie-row-${title?.toLowerCase().replace(/\s+/g, '-')}`}>
      {/* Title */}
      <h2 className="category-title">{title}</h2>

      {/* Scroll Container */}
      <div className="relative">
        {/* Left Arrow */}
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-12 h-full bg-gradient-to-r from-[#030014] to-transparent opacity-70 hover:opacity-100 transition-opacity flex items-center justify-start"
            data-testid={`scroll-left-${title?.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
        )}

        {/* Films */}
        <div
          ref={scrollRef}
          className="scroll-rail hide-scrollbar"
          data-testid={`scroll-container-${title?.toLowerCase().replace(/\s+/g, '-')}`}
        >
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <MovieCardSkeleton key={i} />
            ))
          ) : films.length > 0 ? (
            films.map((film) => (
              <MovieCard key={film.film_id} film={film} />
            ))
          ) : (
            <div className="w-full py-8 text-center text-gray-400">
              No films found
            </div>
          )}
        </div>

        {/* Right Arrow */}
        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-12 h-full bg-gradient-to-l from-[#030014] to-transparent opacity-70 hover:opacity-100 transition-opacity flex items-center justify-end"
            data-testid={`scroll-right-${title?.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <ChevronRight className="w-8 h-8" />
          </button>
        )}
      </div>
    </section>
  );
};
